/**
 * Design tokens taken from docs/design/*.dc.html. Both apps share the light
 * content palette; the console differs only in its dark sidebar and amber mark,
 * the portal in its white sidebar and blue mark.
 */
export const color = {
  text: '#15181E',
  textBody: '#3A4150',
  textMuted: '#5B6270',
  border: '#E1E5EB',
  borderStrong: '#CBD2DD',
  rowRule: '#EEF0F3',
  // Footer strip under a table (role legend).
  subtle: '#FAFBFC',
  page: '#F5F6F8',
  surface: '#FFFFFF',
  link: '#1E56C8',
  linkHover: '#163F94',
  // Portal sidebar and accent.
  portalNavText: '#3A4150',
  portalNavActiveBg: '#EAF0FC',
  portalNavActiveText: '#1E56C8',
  portalMark: '#1E56C8',
  // Console sidebar and accent.
  consoleSidebar: '#13171F',
  consoleNavText: '#C9CFD9',
  consoleNavActiveBg: '#262E3B',
  consoleNavActiveText: '#FFFFFF',
  consoleFooterText: '#9AA3B2',
  consoleMark: '#F2A93B',
  consolePasskeyOk: '#7FCB9B',
  // Staff sign-in page (dark).
  signInPage: '#0E1117',
  signInPanel: '#161B24',
  signInPanelBorder: '#262E3B',
  signInInputBorder: '#3A4352',
  signInRule: '#2A3240',
  signInText: '#E6E9EE',
  signInHeading: '#FFFFFF',
  signInLabel: '#B7BFCC',
  signInMuted: '#7D8696',
  signInLink: '#F2C27A',
  signInError: '#F2A0A0',
  // Unread-count dot on the notifications bell.
  badge: '#C42B2B',
  // Text on the crash-detected banner (its fill is status.down.bg).
  crashBannerText: '#6E1414',
} as const;

/**
 * Status chip palette: background and foreground per state. `blu` and `vio`
 * are category tones (inbox thread and announcement kinds), never a health state.
 */
export const status = {
  ok: { bg: '#E3F2E9', fg: '#17693A' },
  warn: { bg: '#FCEFD6', fg: '#8A4B00' },
  down: { bg: '#FBE4E4', fg: '#A82222' },
  unk: { bg: '#ECEEF2', fg: '#4A5160' },
  blu: { bg: '#E6EDFB', fg: '#1E4FB5' },
  vio: { bg: '#EFE9FB', fg: '#5B3AA6' },
} as const;

/** Dark block for crash output and stack traces. */
export const codeBlock = { bg: '#15181E', fg: '#E6E9EE' } as const;

export type StatusTone = keyof typeof status;

/**
 * Staff-only internal notes: the note form and each note in a conversation.
 * `dashed` edges the form and its collapsed button; `rule` divides inside the form.
 */
export const internalNote = {
  bg: '#FFF8E6',
  border: '#F1E2B8',
  dashed: '#D9B95E',
  rule: '#E8D49A',
  fg: '#5A3A05',
  icon: '#7A4F08',
  button: '#F6E3AE',
} as const;

/** Fill for one day or minute in an uptime strip. */
export const strip = {
  up: '#2E9E5B',
  degraded: '#E0A030',
  down: '#C93C3C',
  noData: '#CBD2DD',
  centralDown: '#9AA3B2',
  maintenance: '#6F8FC7',
} as const;

export type StripState = keyof typeof strip;

export const font = {
  sans: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
} as const;

export const radius = { card: 12, control: 8, chip: 999 } as const;
